import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Build as BuildIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../../api';
import { showSuccess, showError, showDeleteConfirmation } from '../../../utils/sweetAlertConfig';
import PageHeader from '../../../components/PageHeader';
import CreateExamModal from '../../../components/CreateExamModal';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";

export default function Assessments() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const response = await api.get('/exams');
      setExams(response.data || []);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
      showError('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (examId) => {
    const confirmed = await showDeleteConfirmation(
      'Are you sure you want to delete this exam?',
      'This action cannot be undone.'
    );

    if (confirmed.isConfirmed) {
      try {
        await api.delete(`/exams/${examId}`);
        showSuccess('Exam deleted successfully');
        fetchExams();
      } catch (error) {
        showError('Failed to delete exam');
      }
    }
  };

  const handleBuildExam = (examId) => {
    // Open in popup window (standalone mode)
    const width = 1400;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/assessment-builder/${examId}?standalone=true`,
      'AssessmentBuilder',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  const filteredExams = exams.filter(exam =>
    exam.title?.toLowerCase().includes(search.toLowerCase()) ||
    exam.exam_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Digital Assessments"
        subtitle="Create and manage digital exams"
      />

      {/* Action Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search exams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateModal(true)}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#1a1635' } }}
        >
          Create Exam
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchExams}
        >
          Refresh
        </Button>
      </Box>

      {/* Exams Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Exam ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration (min)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Passing Score</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Questions</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredExams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      No exams found. Create your first exam to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredExams.map((exam) => (
                  <TableRow key={exam.exam_id} hover>
                    <TableCell>{exam.exam_id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {exam.title}
                      </Typography>
                      {exam.description && (
                        <Typography variant="caption" color="text.secondary">
                          {exam.description.substring(0, 50)}
                          {exam.description.length > 50 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{exam.duration}</TableCell>
                    <TableCell>{exam.passing_score}%</TableCell>
                    <TableCell>
                      <Chip
                        label={exam.questions_count || 0}
                        size="small"
                        color={exam.questions_count > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={exam.is_published ? 'Published' : 'Draft'}
                        size="small"
                        color={exam.is_published ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Build Exam">
                          <IconButton
                            size="small"
                            onClick={() => handleBuildExam(exam.exam_id)}
                            sx={{ color: PRIMARY }}
                          >
                            <BuildIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(exam.exam_id)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Exam Modal */}
      <CreateExamModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchExams();
        }}
      />
    </Container>
  );
}
